import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import { Send, Bot, User, Loader2, RefreshCw, Copy, Share2, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { User as FirebaseUser } from 'firebase/auth';
import { ConfirmDialog, VideoSection } from './UI';
import { getUsageCount, incrementUsage, QUOTA_AI_ADVISOR } from '../utils/usageLimit';
import { findFAQ, seedInitialFAQs, saveFAQ } from '../lib/faq';
import { saveAIChatMessage, getAIChatHistory, clearAIChatHistory } from '../lib/history';
import { VideoItem } from '../types';

interface Message {
  role: 'user' | 'model';
  text: string;
  image?: string;
}

interface AIAdvisorProps {
  user: FirebaseUser | null;
  language: 'en' | 'te' | 'hi';
  videos: VideoItem[];
}

const AIAdvisor: React.FC<AIAdvisorProps> = ({ user, language, videos }) => {
  const t = {
    en: {
      welcome: 'Hello! I am your Prakruthi Mithra AI Advisor. How can I help you with natural farming, crop management, and biological control methods?',
      clearChat: 'Clear Chat',
      clearConfirm: 'Do you want to clear the chat?',
      thinking: 'Thinking...',
      placeholder: 'Type your question here...',
      copy: 'Copy',
      share: 'Share',
      linkCopied: 'Link copied!',
      relatedVideos: 'Related Videos',
      error: 'Sorry, a technical problem occurred. Please try again later.',
      noResponse: 'Sorry, I am unable to answer. Please try again.',
      advisor: 'AI Advisor',
      limitReached: 'Daily limit reached. You can use this feature 3 times per day.',
      remainingUses: 'Remaining uses today: {count}',
      limitReachedAlert: 'You have used your 3 free AI Advisor daily queries. Please try again tomorrow!',
      quotaExhausted: 'Sorry, the server is busy because too many farmers are currently using it. Please try again after 10 minutes.'
    },
    te: {
      welcome: 'నమస్కారం! నేను మీ ప్రకృతి మిత్ర AI సలహాదారుని. సహజ వ్యవసాయం, పంటల యాజమాన్యం మరియు జీవ నియంత్రణ పద్ధతుల గురించి మీకు ఎలా సహాయపడగలను?',
      clearChat: 'చాట్ క్లియర్ చేయండి',
      clearConfirm: 'సంభాషణను తొలగించాలనుకుంటున్నారా?',
      thinking: 'ఆలోచిస్తున్నాను...',
      placeholder: 'మీ ప్రశ్నను ఇక్కడ టైప్ చేయండి...',
      copy: 'కాపీ',
      share: 'షేర్',
      linkCopied: 'లింక్ కాపీ చేయబడింది!',
      relatedVideos: 'సంబంధిత వీడియోలు',
      error: 'క్షమించండి, సాంకేతిక సమస్య ఏర్పడింది. దయచేసి కాసేపటి తర్వాత మళ్ళీ ప్రయత్నిండి.',
      noResponse: 'క్షమించండి, నేను సమాధానం ఇవ్వలేకపోతున్నాను. దయచేసి మళ్ళీ ప్రయత్నించండి.',
      advisor: 'AI సలహాదారు',
      limitReached: 'రోజువారీ పరిమితి ముగిసింది. మీరు ఈ సేవను రోజుకు 3 సార్లు మాత్రమే ఉపయోగించగలరు.',
      remainingUses: 'ఈరోజు మిగిలి ఉన్న ఉపయోగాల సంఖ్య: {count}',
      limitReachedAlert: 'మీరు ఈరోజుకు మీ 3 ఉచిత AI సలహాదారు క్రెడిట్‌లను ఉపయోగించారు. దయచేసి రేపు మళ్లీ ప్రయత్నించండి!',
      quotaExhausted: 'క్షమించండి, ప్రస్తుతం చాలా మంది రైతులు దీనిని ఉపయోగిస్తున్నందున సర్వర్ బిజీగా ఉంది. దయచేసి 10 నిమిషాల తర్వాత మళ్లీ ప్రయత్నించండి.'
    },
    hi: {
      welcome: 'नमस्ते! मैं आपका प्रकृति मित्र AI सलाहकार हूँ। मैं प्राकृतिक खेती, फसल प्रबंधन और जैविक नियंत्रण विधियों में आपकी कैसे मदद कर सकता हूँ?',
      clearChat: 'चैट साफ़ करें',
      clearConfirm: 'क्या आप चैट साफ़ करना चाहते हैं?',
      thinking: 'सोच रहा हूँ...',
      placeholder: 'अपना प्रश्न यहाँ टाइप करें...',
      copy: 'कॉपी करें',
      share: 'साझा करें',
      linkCopied: 'लिंक कॉपी किया गया!',
      relatedVideos: 'संबंधित वीडियो',
      error: 'क्षमा करें, एक तकनीकी समस्या आई है। कृपया बाद में पुनः प्रयास करें।',
      noResponse: 'क्षमा करें, मैं उत्तर देने में असमर्थ हूँ। कृपया पुनः प्रयास करें।',
      advisor: 'AI सलाहकार',
      limitReached: 'दैनिक सीमा समाप्त। आप इस सुविधा का उपयोग दिन में केवल 3 बार कर सकते हैं।',
      remainingUses: 'आज शेष उपयोग: {count}',
      limitReachedAlert: 'आने आज के लिए अपने 3 मुफ्त AI सलाहकार क्रेडिट का उपयोग कर लिया है। कृपया कल पुनः प्रयास करें!',
      quotaExhausted: 'क्षमा करें, वर्तमान में बहुत अधिक किसान इसका उपयोग कर रहे हैं, इसलिए सर्वर व्यस्त है। कृपया 10 मिनट के बाद पुनः प्रयास करें।'
    }
  }[language];

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [usageCount, setUsageCount] = useState<number>(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const init = async () => {
      // Seed FAQs if none exist
      await seedInitialFAQs(user?.email);
      
      const usageCount = await getUsageCount(user ? user.uid : null, 'aiAdvisor');
      setUsageCount(usageCount);

      if (user) {
        const history = await getAIChatHistory(user.uid);
        if (history.length > 0) {
          setMessages(history.map(m => ({ role: m.role, text: m.text, image: m.image })));
        } else {
          setMessages([{ role: 'model', text: t.welcome }]);
        }
      } else {
        setMessages([{ role: 'model', text: t.welcome }]);
      }
    };
    init();
  }, [user, language]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleClearChat = () => {
    setShowClearConfirm(true);
  };

  const confirmClearChat = async () => {
    if (user) {
      await clearAIChatHistory(user.uid);
    }
    setMessages([
      {
        role: 'model',
        text: t.welcome
      }
    ]);
    setShowClearConfirm(false);
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleShare = async (text: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Prakruthi Mithra AI Advice',
          text: text,
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      handleCopy(text, -1);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    // Check usage limits
    const currentCount = await getUsageCount(user ? user.uid : null, 'aiAdvisor');
    if (currentCount >= QUOTA_AI_ADVISOR) {
      setUsageCount(currentCount);
      alert(t.limitReachedAlert);
      return;
    }

    const userMessage = input;
    setInput('');
    const updatedMessages: Message[] = [...messages, { role: 'user', text: userMessage }];
    setMessages(updatedMessages);
    setIsLoading(true);

    if (user) {
      await saveAIChatMessage(user.uid, 'user', userMessage);
    }

    try {
      // Check for FAQs first
      const faq = await findFAQ(userMessage);
      if (faq) {
        const faqResponse = language === 'te' ? faq.answer_te : language === 'hi' ? faq.answer_hi : faq.answer_en;
        setMessages(prev => [...prev, { role: 'model', text: faqResponse }]);
        if (user) {
          await saveAIChatMessage(user.uid, 'model', faqResponse);
        }
        setIsLoading(false);
        return;
      }

      const contents: any[] = [];
      
      // Filter messages to ensure the conversation starts with a 'user' role
      // and alternates correctly. Gemini generateContent requires the first message to be 'user'.
      const filteredMessages = updatedMessages.filter((m, i) => {
        if (i === 0 && m.role === 'model') return false;
        return true;
      });

      filteredMessages.forEach(m => {
        const parts: any[] = [];
        if (m.text) parts.push({ text: m.text });
        if (m.image) {
          const mimeType = m.image.split(';')[0].split(':')[1] || "image/jpeg";
          parts.push({
            inlineData: {
              mimeType,
              data: m.image.split(',')[1]
            }
          });
        }
        if (parts.length > 0) {
          contents.push({ role: m.role, parts });
        }
      });

      const res = await fetch("/api/gemini/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          model: "gemini-flash-latest",
          contents,
          config: {
            systemInstruction: `You are "Prakruthi Mithra AI Advisor", the ultimate expert in Prakruthi Vyavasayam (Natural Farming).
            
            Your core principles:
            1. ZERO CHEMICALS: Never recommend synthetic fertilizers or pesticides.
            2. BIOLOGICAL CONTROL: Prioritize NPV, Trichoderma, Pseudomonas, Beauveria, and Metarhizium.
            3. KASHAYAMS: Recommend Agniastram, Brahmastram, Neem oil, etc.
            4. SOIL HEALTH: Focus on Jeevamrutham and Ghanajeevamrutham.
            
            Specific Guidance:
            - Spodoptera NPV is ONLY for Spodoptera (Tobacco Caterpillar).
            - Helicoverpa NPV is ONLY for Helicoverpa (Gram Pod Borer).
            - Trichoderma Viride is for soil-borne diseases like Root Rot and Wilt.
            - Pseudomonas is for Blast and Leaf Spots.
            - Beauveria is for Sucking Pests (Aphids, Jassids).
            - Sour Buttermilk (పుల్లటి మజ్జిగ) is excellent for Powdery Mildew.
            
            Tone & Language:
            - Respond ONLY in ${language === 'te' ? 'Telugu' : language === 'hi' ? 'Hindi' : 'English'}.
            - Do NOT use any other language. This is critical. Even if the user asks in another language, you must respond in ${language === 'te' ? 'Telugu' : language === 'hi' ? 'Hindi' : 'English'}.
            - Be extremely respectful, using "${language === 'te' ? 'రైతు సోదరా' : language === 'hi' ? 'किसान भाई' : 'Farmer brother'}".
            - Keep instructions step-by-step and easy to follow.
            - If a farmer asks for a chemical solution, explain the damage it causes to soil and health, then provide the natural alternative.`,
          }
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed securely generating advisor response.");
      }

      const response = await res.json();
      const aiResponse = response.text || t.noResponse;
      setMessages(prev => [...prev, { role: 'model', text: aiResponse }]);

      if (user) {
        await saveAIChatMessage(user.uid, 'model', aiResponse);
      }

      // Save as FAQ for future use if it's a good response
      if (aiResponse !== t.noResponse) {
        await saveFAQ(userMessage, aiResponse, language);
      }

      // Dynamically increment usage and update local state
      const newCount = await incrementUsage(user ? user.uid : null, 'aiAdvisor');
      setUsageCount(newCount);
    } catch (error: any) {
      console.error("AI Error:", error);
      
      const errStr = String(error?.message || error || '').toLowerCase();
      const isQuotaExhausted = errStr.includes('resource_exhausted') || errStr.includes('429') || errStr.includes('quota');
      
      let errorMessage = isQuotaExhausted ? t.quotaExhausted : t.error;
      
      if (!isQuotaExhausted && error?.message) {
        errorMessage += ` (Error: ${error.message})`;
      }
      
      setMessages(prev => [...prev, { role: 'model', text: errorMessage }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#f8f9fa]" id="ai-advisor-container">
      {/* Header with Clear Chat */}
      <div className="px-4 py-2 bg-white border-b border-black/5 flex justify-between items-center">
        <div className="flex items-center gap-2 text-[#1b5e20]">
          <Bot size={20} />
          <span className="text-sm font-bold">{t.advisor}</span>
        </div>
        <button 
          onClick={handleClearChat}
          className="text-xs font-bold text-stone-400 hover:text-rose-500 flex items-center gap-1 transition-colors"
        >
          <RefreshCw size={14} /> {t.clearChat}
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <AnimatePresence initial={false}>
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex flex-col gap-2 max-w-[90%] ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                {msg.image && (
                  <div className="p-3 bg-[#1b5e20] rounded-[24px] shadow-md">
                    <img 
                      src={msg.image} 
                      alt="User uploaded" 
                      className="max-w-full h-auto rounded-[16px] object-cover"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                )}
                
                {msg.text && (
                  <div className={`relative p-4 rounded-[24px] shadow-sm text-sm leading-relaxed ${
                    msg.role === 'user' 
                      ? 'bg-[#1b5e20] text-white' 
                      : 'bg-white text-[#1a1a1a] border border-black/5'
                  }`}>
                    <div className="markdown-body">
                      <Markdown>{msg.text}</Markdown>
                    </div>
                    {msg.role === 'model' && (
                      <div className="flex justify-end mt-2 gap-3">
                        <button 
                          onClick={() => handleCopy(msg.text, idx)}
                          className={`transition-colors ${copiedIndex === idx ? 'text-emerald-500' : 'text-stone-400 hover:text-[#1b5e20]'}`}
                          title={t.copy}
                        >
                          {copiedIndex === idx ? <Check size={18} /> : <Copy size={18} />}
                        </button>
                        <button 
                          onClick={() => handleShare(msg.text)}
                          className="text-stone-400 hover:text-[#1b5e20] transition-colors"
                          title={t.share}
                        >
                          <Share2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white p-4 rounded-[24px] shadow-sm border border-black/5 flex items-center gap-3 text-[#1b5e20]">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-xs font-medium">{t.thinking}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
        <VideoSection videos={videos} placement="advisor" t={t} />
      </div>

      {/* Remaining Daily Limits Banner */}
      <div className="px-5 py-2.5 bg-stone-50 border-t border-black/5 flex justify-between items-center text-xs font-medium text-stone-500">
        <span className={usageCount >= QUOTA_AI_ADVISOR ? 'text-rose-500 font-semibold md:text-sm' : ''}>
          {usageCount >= QUOTA_AI_ADVISOR 
            ? t.limitReached 
            : t.remainingUses.replace('{count}', String(QUOTA_AI_ADVISOR - usageCount))}
        </span>
        <span className="text-[10px] text-stone-400 font-mono">
          {usageCount}/{QUOTA_AI_ADVISOR}
        </span>
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-black/5">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={usageCount >= QUOTA_AI_ADVISOR ? t.limitReached : t.placeholder}
              disabled={usageCount >= QUOTA_AI_ADVISOR}
              className="w-full bg-[#f8f9fa] border border-black/5 rounded-full px-6 py-3 text-sm focus:ring-2 focus:ring-[#1b5e20] outline-none disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim() || usageCount >= QUOTA_AI_ADVISOR}
            className="bg-[#1b5e20] text-white p-3 rounded-full hover:bg-[#144317] disabled:opacity-50 transition-all shadow-md active:scale-95"
          >
            <Send size={24} />
          </button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showClearConfirm}
        title={t.clearChat}
        message={t.clearConfirm}
        onConfirm={confirmClearChat}
        onCancel={() => setShowClearConfirm(false)}
        type="danger"
      />
    </div>
  );
};

export default AIAdvisor;
